import {
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  InputNumber,
  Modal,
  Spin,
} from 'antd';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import apiService from '../api/apiService';
import GuestModel from '../components/GuestModel';
import './AvailModel.css';
import styles from '../components/Guest.module.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { min } from 'moment';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);

const { Option } = Select;

const AvailModel = ({ isOpen, onClose, hotelId }) => {
  const [form] = Form.useForm();
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomPrices, setRoomPrices] = useState([]);
  const [guests, setGuests] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [selectedRoomPrice, setSelectedRoomPrice] = useState([]);
  const [selectedPlanPrice, setSelectedPlanPrice] = useState(0);
  const [selectedExtraBedPrice, setSelectedExtraBedPrice] = useState(0);
  const [allRooms, setAllRooms] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [selectedPlanExtraBedPrice, setSelectedPlanExtraBedPrice] = useState(0);
  const [totalExtraBedsPrice, setTotalExtraBedsPrice] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [newGuest, setNewGuest] = useState('');
  const [availData, setAvailData] = useState([]);
  const [options, setOptions] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState(0);
  const [cwobPlanPrice, setCwobPlanPrice] = useState(0);
  const [totalCwobPrice, setTotalCwobPrice] = useState(0);

  const addAvailData = newData => {
    setAvailData(prevData => [...prevData, newData]);
  };

  useEffect(() => {
    fetchBookings();
    fetchRooms();
    fetchRoomPrices();
    fetchPlanTypes();
    /*     fetchExtraBeds();
     */
  }, []);

  useEffect(() => {
    fetchGuests();
  }, []);

  useEffect(() => {
    calculateTotalAmount();
  }, [
    checkInDate,
    checkOutDate,
    selectedRoomPrice,
    selectedPlanPrice,
    totalExtraBedsPrice,
    totalCwobPrice,
  ]);

  // Update available rooms when dates change
  useEffect(() => {
    if (checkInDate && checkOutDate) {
      setCheckingAvailability(true);
      // Reset room selection when dates change
      form.setFieldsValue({ roomType: undefined });
      setSelectedRoomPrice([]);

      // Short timeout to show loading state
      setTimeout(() => {
        const available = allRooms
          .filter(room =>
            isRoomAvailable(checkInDate, checkOutDate, room.bookedDates)
          )
          .map(room => ({
            ...room,
            status: 'Available',
          }));

        setAvailableRooms(available);

        const roomOptions = available
          .sort((a, b) => parseInt(a.room_number) - parseInt(b.room_number))
          .map(availableRoom => ({
            label: `Room ${availableRoom.room_number} - ${availableRoom.room_type.name}`,
            value: availableRoom.id,
          }));

        setOptions(roomOptions);
        setCheckingAvailability(false);
      }, 300);
    } else {
      setAvailableRooms([]);
    }
  }, [checkInDate, checkOutDate, allRooms, form]);

  // Show notification when no rooms available
  /* useEffect(() => {
    if (
      checkInDate &&
      checkOutDate &&
      !checkingAvailability &&
      availableRooms.length === 0
    ) {
      toast.warning('No rooms available for the selected dates');
    }
  }, [availableRooms, checkInDate, checkOutDate, checkingAvailability]); */

  const fetchBookings = async () => {
    try {
      const response = await apiService.get(`/bookings/?hotel=${hotelId}`);
      setBookings(response);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await apiService.get(`/rooms/?hotel=${hotelId}`);
      setRooms(response);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await apiService.get(`/guests/?hotel=${hotelId}`);
      setGuests(response);
    } catch (error) {
      console.error('Error fetching guests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomPrices = async () => {
    try {
      const response = await apiService.get(`/room-prices/?hotel=${hotelId}`);
      setRoomPrices(response);
    } catch (error) {
      console.error('Error fetching room prices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanTypes = async () => {
    try {
      const response = await apiService.get(`/plan-type/?hotel=${hotelId}`);
      setPlanTypes(response);
    } catch (error) {
      console.error('Error fetching plan types:', error);
    } finally {
      setLoading(false);
    }
  };

  /* const fetchExtraBeds = async () => {
    try {
      const response = await apiService.get(`/extra-bed/?hotel=${hotelId}`);
      setExtraBeds(response);
    } catch (error) {
      console.error('Error fetching extra beds:', error);
    } finally {
      setLoading(false);
    }
  }; */

  const calculateTotalAmount = () => {
    if (checkInDate && checkOutDate) {
      const duration = checkOutDate.diff(checkInDate, 'days');
      const pricePerNight = parseFloat(selectedRoomPrice) || 0;
      const planPrice = parseFloat(selectedPlanPrice) || 0;
      const extraBedPrice = parseFloat(totalExtraBedsPrice) || 0;
      const cwobPrice = parseFloat(totalCwobPrice || 0);

      if (duration > 0) {
        /* const totalAmount =
          pricePerNight * duration + planPrice + extraBedPrice * duration; */
        const totalAmount =
          (pricePerNight + planPrice + extraBedPrice + cwobPrice) * duration;
        form.setFieldsValue({ amount: totalAmount });
      } else {
        form.setFieldsValue({ amount: '' });
      }
    } else {
      form.setFieldsValue({ amount: '' });
    }
  };

  const fetchRoomsAndBookings = async () => {
    try {
      const roomsResponse = await apiService.get(`/rooms/?hotel=${hotelId}`);
      const bookingsResponse = await apiService.get(
        `/bookings/?hotel=${hotelId}`
      );

      const enrichedRooms = enrichRoomsWithBookings(
        roomsResponse,
        bookingsResponse
      );
      setAllRooms(enrichedRooms);
    } catch (error) {
      console.error('Error fetching rooms or bookings:', error);
      toast.error('Failed to fetch rooms and bookings');
    }
  };

  // Fetch rooms and bookings on component mount
  useEffect(() => {
    fetchRoomsAndBookings();
  }, []);

  function isRoomAvailable(startDate, endDate, bookedDates) {
    if (!startDate || !endDate) return false;

    const start = dayjs(startDate).startOf('day');
    const end = dayjs(endDate).startOf('day');

    const activeBookings = bookedDates.filter(b => b.status !== 'cancelled');

    for (let booking of activeBookings) {
      const bookingStart = dayjs(booking.start).startOf('day');
      const bookingEnd = dayjs(booking.end).startOf('day');

      const overlaps =
        start.isSame(bookingStart) ||
        start.isBetween(bookingStart, bookingEnd, null, '[]') ||
        end.isSame(bookingStart) ||
        end.isBetween(bookingStart, bookingEnd, null, '[]') ||
        (start.isBefore(bookingStart) && end.isAfter(bookingEnd));

      if (overlaps) return false;
    }

    return true;
  }

  function enrichRoomsWithBookings(allRooms, allBookings) {
    return allRooms.map(room => {
      const bookingsForRoom = allBookings.filter(bk =>
        bk.room.some(r => r.id === room.id)
      );

      const bookedDates = bookingsForRoom.map(bk => ({
        id: bk.id,
        start: bk.check_in,
        end: bk.check_out,
        status: bk.status,
      }));

      return {
        ...room,
        bookedDates,
      };
    });
  }

  const getRoomPrice = async roomId => {
    try {
      const matchedPrice = roomPrices.find(price => price.room === roomId);
      return matchedPrice ? parseFloat(matchedPrice.amount) : 0;
    } catch (error) {
      console.error('ERROR fetching room prices:', error);
      return 0;
    }
  };

  const handleCheckInChange = date => {
    setCheckInDate(date ? dayjs(date) : null);

    if (checkOutDate && date && dayjs(checkOutDate).isSameOrBefore(date)) {
      setCheckOutDate(null);
      form.setFieldsValue({ checkOut: null });
    }

    // Reset room selection
    form.setFieldsValue({ roomType: undefined });
    setSelectedRoomPrice(0);
  };

  const handleCheckOutChange = date => {
    setCheckOutDate(date ? dayjs(date) : null);

    // Reset room selection
    form.setFieldsValue({ roomType: undefined });
    setSelectedRoomPrice(0);
  };

  const handleRoomSelection = async roomIds => {
    setSelectedRooms(roomIds.length);
    if (!roomIds || roomIds.length === 0) {
      setSelectedRoomPrice(0);
      form.setFieldsValue({ amount: '' });
      return;
    }

    let totalPrice = 0;
    for (const roomId of roomIds) {
      const price = await getRoomPrice(roomId);
      totalPrice += price;
    }

    setSelectedRoomPrice(totalPrice);
    form.setFieldsValue({ amount: totalPrice });
  };

  const handlePlanTypeChange = planId => {
    if (!planId) {
      setSelectedPlanPrice(0);
      setSelectedPlanExtraBedPrice(0);
      setTotalExtraBedsPrice(0);
      setCwobPlanPrice(0);
      setSelectedPlan(false);
      form.setFieldsValue({ extra_bed: undefined }); // 👈 Resets extra bed field
      return;
    }

    setSelectedPlan(true);

    const matchedPlan = planTypes.find(plan => plan.id === planId);
    if (matchedPlan) {
      setSelectedPlanPrice(parseFloat(matchedPlan.price * selectedRooms));
      setSelectedPlanExtraBedPrice(matchedPlan.extra_bed_price);
      setCwobPlanPrice(matchedPlan.cwob_price);
    } else {
      setSelectedPlanPrice(0);
      setSelectedPlanExtraBedPrice(0);
    }
  };

  const handleExtraBedChange = value => {
    const totalPrice = value * selectedPlanExtraBedPrice;
    setTotalExtraBedsPrice(totalPrice);
  };

  const handleCwobChange = value => {
    const totalPrice = value * cwobPlanPrice;
    setTotalCwobPrice(totalPrice);
  };

  const handleSubmit = async values => {
    try {
      const bookingData = {
        room_ids: values.roomType,
        check_in: values.checkIn.format('YYYY-MM-DD'),
        check_out: values.checkOut.format('YYYY-MM-DD'),
        source: values.source,
        status: values.status,
        amount: values.amount ? values.amount.toString() : '',
        extra_bed: values.extraBed || null,
        cwob: values.cwob,
        discount: values.discount,
        advance: values.advance,
        guest: values.guestInfo,
        plan_type: values.plan,
        hotel: hotelId,
      };

      // Determine status to set on the room(s)
      const newRoomStatus =
        values.status === 'confirmed' ? 'occupied' : 'available';

      if (bookingData.room_ids && Array.isArray(bookingData.room_ids)) {
        for (const roomId of bookingData.room_ids) {
          await updateRoomStatus(roomId, newRoomStatus);
        }
      } else if (bookingData.room) {
        await updateRoomStatus(bookingData.room, newRoomStatus);
      }

      await apiService.post('/bookings/', bookingData);
      fetchRoomsAndBookings();
      toast.success('Booking created successfully');
      form.resetFields();
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error while submitting form:', error);
      toast.error('Failed to create booking');
    }
  };

  const updateRoomStatus = async (roomId, newStatus) => {
    try {
      const updatedRoomStatus = { status: newStatus };

      await apiService.patch(
        `/rooms/${roomId}/?hotel=${hotelId}`,
        updatedRoomStatus
      );
    } catch (error) {
      console.error(`Failed to update room ${roomId} status:`, error);
      toast.error(`Failed to update room ${roomId} status!`);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  /* const handleGuestAdded = () => {
    fetchGuests();
  };

  const handleChange = value => {
    console.log(`selected ${value}`);
  }; */

  return (
    <>
      <Button type="primary" onClick={() => setIsModalVisible(true)}>
        Create Booking
      </Button>
      <Modal
        title="Room Availability Form"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={1000}
      >
        <Form
          clearOnDestroy
          form={form}
          onFinish={handleSubmit}
          className="avail-form"
          width={1000}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Check In"
                name="checkIn"
                rules={[
                  { required: true, message: 'Please select check-in date' },
                ]}
              >
                <DatePicker
                  placeholder="Select check-in date"
                  format="YYYY/MM/DD"
                  style={{ width: '100%' }}
                  /* disabledDate={current =>
                    current && current < dayjs().startOf('day')
                  } */
                  onChange={handleCheckInChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Check Out"
                name="checkOut"
                rules={[
                  { required: true, message: 'Please select check-out date' },
                ]}
              >
                <DatePicker
                  placeholder="Select check-out date"
                  format="YYYY/MM/DD"
                  style={{ width: '100%' }}
                  /* disabledDate={current =>
                    checkInDate &&
                    current &&
                    current <= checkInDate.startOf('day')
                  } */
                  onChange={handleCheckOutChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Room"
                name="roomType"
                rules={[{ required: true, message: 'Please select a room' }]}
              >
                <Select
                  placeholder={
                    !checkInDate || !checkOutDate
                      ? 'Select dates first'
                      : checkingAvailability
                      ? 'Checking availability...'
                      : options.length === 0
                      ? 'No rooms available for selected dates'
                      : 'Select room'
                  }
                  disabled={
                    !checkInDate ||
                    !checkOutDate ||
                    checkingAvailability ||
                    options.length === 0
                  }
                  onChange={handleRoomSelection}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  /* onChange={handleChange} */
                  options={options}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Plan"
                name="plan"
                rules={[{ required: true, message: 'Please select a plan' }]}
              >
                <Select
                  placeholder="Select plan"
                  loading={loading}
                  allowClear
                  style={{ marginBottom: '8px' }}
                  onChange={handlePlanTypeChange}
                  // /* showSearch */
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {planTypes.map(plan => (
                    <Option key={plan.id} value={plan.id}>
                      {plan.title}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="extraBed" label="Extra Bed">
                {!selectedPlan ? (
                  <Select placeholder="Select plan first" disabled />
                ) : (
                  <InputNumber
                    min={0}
                    max={5}
                    style={{ width: '100%' }}
                    disabled={!selectedPlan}
                    onChange={handleExtraBedChange}
                    placeholder="Select no. of extra beds"
                    loading={checkingAvailability}
                  />
                )}
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="CWOB"
                name="cwob"
                rules={[
                  { required: true, message: 'Please select no. of CWOB' },
                ]}
              >
                <InputNumber
                  min={0}
                  max={5}
                  placeholder="Select no. of CWOB"
                  onChange={handleCwobChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Source"
                name="source"
                rules={[{ required: true, message: 'Please select source' }]}
              >
                <Select
                  placeholder="Select source"
                  style={{ marginBottom: '8px' }}
                >
                  <Option value="website">Website</Option>
                  <Option value="agent">Agent</Option>
                  <Option value="walk_in">Walk-in</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select
                  placeholder="Select status"
                  style={{ marginBottom: '8px' }}
                >
                  <Option value="pending">Pending</Option>
                  <Option value="confirmed">Confirmed</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{ justifyContent: 'start', marginBottom: '20px' }}
            className={styles.buttonContainer}
          >
            <button
              className={styles.addGuestButton}
              type="button"
              onClick={() => setIsModelOpen(true)}
            >
              Add Guests
            </button>
          </div>

          <GuestModel
            isOpen={isModelOpen}
            onClose={() => setIsModelOpen(false)}
            newGuest={newGuest}
            setNewGuest={setNewGuest}
            onGuestAdded={fetchGuests}
            hotelId={hotelId}
          />

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Guest Info"
                name="guestInfo"
                rules={[{ required: true, message: 'Please select guest(s)' }]}
              >
                <Select
                  placeholder="Select guests"
                  loading={loading}
                  allowClear
                  style={{ marginBottom: '8px' }}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {guests.map(guest => (
                    <Option key={guest.id} value={guest.id}>
                      {guest.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label="Amount"
                name="amount"
                rules={[{ required: true, message: 'Please enter amount' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0"
                  min={0}
                  controls={false}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Advance Paid" name="advance">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter advance amount paid (optional)"
                  controls={false}
                  min={0}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item label="Discount" name="discount">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter discount (optional)"
                  controls={false}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col xs={24}>
              <div className="avail-button-group">
                <Button
                  type="default"
                  onClick={handleCancel}
                  className="avail-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="avail-save-btn"
                >
                  Save
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default AvailModel;
